import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import classNames from 'classnames';
import styled from 'styled-components';

import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useWidgetDataFetcher } from 'hooks/useWidgetDataFetcher';
import { useAppSelector } from 'store/hook';

import GraphWidgetContentGraph from './GraphWidgetContentGraph';
import GraphWidgetContentTable from './GraphWidgetContentTable';
import { useOrderedWidgetData } from './hooks';
import { getGroupByPayload } from './utils';
import { WIDGET_TYPES } from '../constants';
import { applyValueDifferenceToWidgetData } from '../valueDifferenceUtils';
import { DATA_DISPLAY_TYPE, DATA_RANGE_TYPE } from '../Widget/constants';
import NoDataInfo from '../Widget/NoDataContent';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  flex-grow: 1;
  height: 100%;
  overflow: hidden;

  &.allow-overflow {
    overflow: unset;
  }
`;

const GraphWidgetContent = () => {
  const { apartmentTypeGroups } = useAppSelector((state) => state.global);
  const { communityId } = useCommunityViewContext();
  const { type, filters, rangeType, groupBy, displayType, priceDisplay } = useWidgetContext();
  const { fetchWidgetData, showContractLabelAnnotations, snapshotUuids, communityContractIds } = useWidgetDataFetcher();
  const { publication } = usePublicationContext();
  const {
    competitors,
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    careLevelsStatsType,
    medicationLevels,
    medicationLevelsStatsType,
    reviewSources,
    reviewCategories,
    swotCategories,
    communityContractType,
    communityContractSelectionType,
    contractTypes,
  } = filters;

  const isRatingWidget = type === WIDGET_TYPES.RATING_GRAPH;

  const { data: widgetData } = useQuery({
    queryKey: [
      type,
      communityId,
      filters,
      rangeType,
      groupBy,
      priceDisplay,
      showContractLabelAnnotations,
      communityContractIds,
    ],
    queryFn: ({ signal }) => {
      const filters = {
        competitors,
        care_types: careTypes,
        room_types: roomTypes,
        room_types_price_display: roomTypesPriceDisplay,
        care_levels: careLevels,
        medication_levels: medicationLevels,
        review_sources: reviewSources,
        review_categories: reviewCategories,
        swot_categories: swotCategories,
        care_levels_stats_type: careLevelsStatsType,
        medication_levels_stats_type: medicationLevelsStatsType,
        community_contract_type: communityContractType,
        community_contract_selection_type: communityContractSelectionType,
        contract_types: contractTypes,
      };

      return fetchWidgetData(
        communityId,
        type,
        rangeType,
        {
          filters,
          group_by: rangeType === DATA_RANGE_TYPE.GROUPED ? getGroupByPayload(groupBy) : undefined,
          price_display: priceDisplay,
          ...(publication?.hash ? { public_hash: publication.hash } : {}),
        },
        { signal }
      );
    },
    refetchOnWindowFocus: false,
    enabled: communityId > 0 && (snapshotUuids !== null || isRatingWidget),
  });

  const data = useOrderedWidgetData(widgetData, rangeType, type, apartmentTypeGroups, groupBy);
  const comparisonTypeEnabled = filters?.comparisonTypeEnabled;
  const valueDisplay = filters?.valueDisplay;

  const dataWithDifferences = useMemo(
    () =>
      applyValueDifferenceToWidgetData(data, {
        rangeType,
        widgetType: type,
        communityId,
        comparisonTypeEnabled,
        valueDisplay,
      }),
    [data, rangeType, type, communityId, comparisonTypeEnabled, valueDisplay]
  );
  const hasData = Array.isArray(dataWithDifferences) && dataWithDifferences.length > 0;
  const allOverFlow = rangeType === DATA_RANGE_TYPE.GROUPED || displayType === DATA_DISPLAY_TYPE.TABLE;

  return (
    <Container className={classNames({ 'allow-overflow': allOverFlow })}>
      {hasData && displayType === DATA_DISPLAY_TYPE.GRAPH && (
        <GraphWidgetContentGraph
          data={dataWithDifferences}
          valueDisplay={valueDisplay}
          comparisonTypeEnabled={comparisonTypeEnabled}
        />
      )}
      {hasData && displayType === DATA_DISPLAY_TYPE.TABLE && (
        <GraphWidgetContentTable
          data={dataWithDifferences}
          valueDisplay={valueDisplay}
          comparisonTypeEnabled={comparisonTypeEnabled}
        />
      )}
      {!hasData && <NoDataInfo>No Data Available</NoDataInfo>}
    </Container>
  );
};

export default GraphWidgetContent;
